import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  BackupPolicyConfig,
  CloudResourceAttachmentEvent,
} from '@repo/api-gw-sdk';
import { CloudResourceAttachmentEventType } from '@repo/api-gw-sdk';

import { BackupPolicyTag } from '@/components/backupPolicy/BackupPolicyTag';
import { dayjs, DATE_TIME_FORMAT } from '@/utils/dayjs';

const PolicyLabel = ({
  policy,
}: {
  policy: BackupPolicyConfig | undefined;
}) => {
  if (!policy) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Deleted
      </Typography>
    );
  }

  return <BackupPolicyTag policy={policy} />;
};

export const AttachmentEventsHistory = ({
  events,
  policies,
  isLoading,
}: {
  events: CloudResourceAttachmentEvent[];
  policies: BackupPolicyConfig[];
  isLoading: boolean;
}) => {
  const policiesMap = new Map(policies.map((p) => [p.id, p]));

  if (isLoading) {
    return (
      <Box className='py-[16px]'>
        <Typography variant='body2' color='text.secondary'>
          Loading history...
        </Typography>
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box className='py-[16px]'>
        <Typography variant='body2' color='text.secondary'>
          No backup policies history available
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: '8px' }}>
              <Typography variant='subtitle2'>TIME (UTC)</Typography>
            </TableCell>
            <TableCell sx={{ padding: '8px' }}>
              <Typography variant='subtitle2'>EVENT</Typography>
            </TableCell>
            <TableCell sx={{ padding: '8px' }}>
              <Typography variant='subtitle2'>BACKUP POLICY</Typography>
            </TableCell>
            <TableCell sx={{ padding: '8px' }}>
              <Typography variant='subtitle2'>REASON</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event, index) => {
            const policy = policiesMap.get(event.policyId);
            const isAttach =
              event.eventType ===
              CloudResourceAttachmentEventType.ATTACHMENT_EVENT_TYPE_ATTACH;

            return (
              <TableRow key={`${event.policyId}-${event.date}-${index}`}>
                <TableCell sx={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  <Typography variant='body2'>
                    {dayjs.utc(event.date).format(DATE_TIME_FORMAT)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '8px' }}>
                  <Typography
                    variant='body2'
                    color={
                      isAttach
                        ? 'var(--mui-palette-success-main)'
                        : 'var(--mui-palette-error-main)'
                    }
                  >
                    {isAttach ? 'Assigned' : 'Removed'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '8px' }}>
                  <PolicyLabel policy={policy} />
                </TableCell>
                <TableCell sx={{ padding: '8px' }}>
                  <Typography variant='body2' color='text.secondary'>
                    {event.reason || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
